import { useEffect, useState, useMemo } from 'react';
import { adminExpenseApi } from '../../lib/api.js';
import { useToast } from '../../hooks/useToast.jsx';
import DataTable from '../../components/ui/DataTable.jsx';
import Button from '../../components/ui/Button.jsx';
import Input from '../../components/ui/Input.jsx';
import Modal from '../../components/ui/Modal.jsx';
import Badge from '../../components/ui/Badge.jsx';
import { Plus, Pencil, Trash2, Info } from 'lucide-react';

export default function PositionsPage() {
  const toast = useToast();
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(null);

  async function load() {
    setLoading(true);
    try {
      const res = await adminExpenseApi.listPositions();
      setPositions(res.positions || []);
    } catch (err) {
      toast.error(`Failed to load positions: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = useMemo(() => {
    if (!search) return positions;
    const s = search.toLowerCase();
    return positions.filter(
      (p) => p.displayName?.toLowerCase().includes(s) || p.rowKey?.toLowerCase().includes(s),
    );
  }, [positions, search]);

  const handleSave = async (positionId, data) => {
    try {
      await adminExpenseApi.upsertPosition(positionId, data);
      toast.success(`Position "${data.displayName}" saved`);
      setEditing(null);
      load();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleDelete = async (positionId, displayName) => {
    if (!confirm(`Deactivate position "${displayName}"?`)) return;
    try {
      await adminExpenseApi.deletePosition(positionId);
      toast.success('Position deactivated');
      load();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const columns = [
    {
      key: 'rowKey',
      label: 'ID',
      render: (p) => <span className="font-mono text-xs">{p.rowKey}</span>,
    },
    {
      key: 'displayName',
      label: 'Name',
      render: (p) => <span className="font-medium">{p.displayName}</span>,
    },
    {
      key: 'isActive',
      label: 'Status',
      render: (p) => (
        <Badge variant={p.isActive !== false ? 'success' : 'default'}>
          {p.isActive !== false ? 'Active' : 'Inactive'}
        </Badge>
      ),
    },
    {
      key: 'actions',
      label: '',
      render: (p) => (
        <div className="flex items-center gap-1 justify-end">
          <button
            onClick={(e) => { e.stopPropagation(); setEditing(p); }}
            className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
          >
            <Pencil className="h-3.5 w-3.5" />
          </button>
          {p.isActive !== false && (
            <button
              onClick={(e) => { e.stopPropagation(); handleDelete(p.rowKey, p.displayName); }}
              className="p-1.5 rounded-lg text-muted-foreground hover:text-profun-pink hover:bg-profun-pink/10 transition-colors"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Positions</h1>
          <p className="text-muted-foreground">{positions.length} positions configured</p>
        </div>
        <Button onClick={() => setEditing({ rowKey: '', displayName: '', isActive: true, isNew: true })}>
          <Plus className="h-4 w-4 mr-2" />
          Add Position
        </Button>
      </div>

      <div className="mb-5 flex items-start gap-2.5 rounded-md border border-border bg-muted/40 p-3 text-sm text-muted-foreground">
        <Info className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
        <p>
          These positions populate the <span className="font-medium text-foreground">Position</span> dropdown
          on the employee Expense Report form. Active positions appear as options;
          deactivated ones are hidden from new reports but stay attached to historical
          ones.
        </p>
      </div>

      <div className="mb-4">
        <Input
          placeholder="Search positions..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="max-w-[300px]"
        />
      </div>

      <DataTable
        columns={columns}
        rows={filtered}
        loading={loading}
        emptyMessage="No positions configured. Add one to populate the expense-report dropdown."
        keyFn={(p) => p.rowKey}
        onRowClick={(p) => setEditing(p)}
      />

      {editing && (
        <PositionModal
          position={editing}
          onClose={() => setEditing(null)}
          onSave={handleSave}
        />
      )}
    </div>
  );
}

function PositionModal({ position, onClose, onSave }) {
  const [positionId, setPositionId] = useState(position.rowKey || '');
  const [displayName, setDisplayName] = useState(position.displayName || '');
  const [isActive, setIsActive] = useState(position.isActive !== false);
  const [saving, setSaving] = useState(false);
  const isNew = !!position.isNew || !position.rowKey;

  const handleSubmit = async () => {
    if (!displayName.trim()) return;
    // Auto-derive a slug from the display name when creating, so the
    // admin doesn't have to type both. Existing positions keep their id.
    const id = isNew
      ? (positionId.trim() || displayName.trim()).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')
      : position.rowKey;
    if (!id) return;
    setSaving(true);
    await onSave(id, { displayName: displayName.trim(), isActive });
    setSaving(false);
  };

  return (
    <Modal isOpen onClose={onClose} title={isNew ? 'Add Position' : 'Edit Position'}>
      <div className="space-y-4">
        <Input
          label="Display Name"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          placeholder="e.g. Senior Associate, Manager, Director"
        />
        <Input
          label="Position ID (slug)"
          value={isNew ? positionId : position.rowKey}
          onChange={(e) => setPositionId(e.target.value)}
          placeholder="Auto-derived from name if left blank"
          disabled={!isNew}
        />
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={isActive}
            onChange={(e) => setIsActive(e.target.checked)}
            className="rounded border-white/20"
          />
          <span className="text-sm">Active</span>
        </label>
        <div className="flex justify-end gap-3 pt-2">
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={saving || !displayName.trim()}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
